import React, {useContext, useEffect, useState} from "react"
import {useTranslation} from "next-i18next"
import PropTypes from "prop-types"
import CheckoutContext from "../../context/CheckoutContext"
import {ArrowLeft, Lock} from "react-bootstrap-icons"
import CartContext from "../../context/CartContext"
import {useRouter} from "next/router"
import {useFormikContext} from "formik"
import Link from "next/link"
import {clarityEvents, ecommerceEvents, gaCategories, gaEvents} from "../../config/googleAnalytics/events"
import Typography from "../reusable/Typography/Typography"
import Button from "../reusable/components/Button"
import {filterUrl} from "../filter/FilterUrl"
import Image from "next/image"
import TermsAndConditionsCheckbox from "./TermsAndConditionsCheckbox"
import {OverviewButtonText} from "../forms/CheckoutFormContactDetails"
import AnalyticsContext from "../../context/AnalyticsContext"
import FirebaseContext from "../../context/FirebaseContext"
import {fetchCheckoutDoc, formatPrice} from "../../utility/Helper"
import {customPlanLengthMap} from "../bikeDetails/plan/customPlanLengthMap"
import DiscountCodeContext from "../../context/DiscountCodeContext"
import OrganizationContext from "../../context/OrganizationContext"
import StockStatusContext from "../../context/StockStatusContext"

type Props = {
    hideButton?: boolean,
    showPriceWarning?: boolean,
    children?: React.ReactNode,
	isCheckoutError?: boolean,
	buttonText?: string,
	isLoading?: boolean,
	termsAndConditions?:boolean,
	checkoutMessage?:boolean,
	showBackLink?:boolean,
	payOneTime?: boolean,
	isNewDesign?: boolean,
	useFirestore?: boolean
}

const Overview: React.FC<Props> = ({hideButton = false, showPriceWarning, isCheckoutError, children, buttonText = "", isLoading = false, termsAndConditions = true, checkoutMessage = true, showBackLink = true, payOneTime = false, isNewDesign = false, useFirestore = false}) => {
	const {t} = useTranslation(["common", "checkout"])
	const {data} = useContext(CheckoutContext)
	const {items} = useContext(CartContext)
	const router = useRouter()
	const {checkoutDocId} = router.query
	const formik = hideButton ? useFormikContext() : null
	const [forwarding, setForwarding] = useState(false)
	const {reactGA} = useContext(AnalyticsContext)
	const {user, db} = useContext(FirebaseContext)
	const [firestoreData, setFirestoreData] = useState<any>(null)
	const checkoutData = useFirestore ? {
		...firestoreData,
		...firestoreData?.checkoutContextData
	} : data
	let totalDownPayment = 0

	const _items = useFirestore ? checkoutData.cartItems : items
	if (useFirestore) {
		totalDownPayment = checkoutData.totalDownPayment
	} else {
		_items?.forEach(item => {
			totalDownPayment += (item.downPayment || 0)
		})
	}

	let counter = 0
	_items?.forEach(item => {
		counter += parseFloat(useFirestore ? item.price : (item.product.finalPriceWithoutDownPayment || item.product.price))
	})
	if (useFirestore) {
		// Fetch Firestore checkout document
		useEffect(() => {
			if (checkoutDocId && user && db) {
				fetchCheckoutDoc(db, checkoutDocId).then(checkoutDoc => setFirestoreData(checkoutDoc))
			}
		}, [checkoutDocId, user, db])
	}

	const {getStockStatus, inStock, showOutOfStockModal, isDoneChecking} = useContext(StockStatusContext)

	useEffect(() => {
		const skus = items.map(item => item?.product?.sku).filter(Boolean)
		getStockStatus(skus)
	}, [items?.length])

	const totalPrice = counter
	const displayPrice = data?.resultingPrice || totalPrice
	const redColor = "#E61C45"
	const isOnSale = items?.some(item => item.isOnSale)
	const {discountCode} = useContext(DiscountCodeContext)
	const {organizationSlug, organizationData} = useContext(OrganizationContext)
	const isCheckoutBeforeProcess = router.pathname === "/checkout" || router.pathname === "/checkoutPersonalData"
	const planLengthMap = customPlanLengthMap[process.env.NEXT_PUBLIC_DISCOUNTS_INSTANCE]
	const planLength = checkoutData?.planLength ? planLengthMap && isCheckoutBeforeProcess ? planLengthMap[checkoutData?.planLength] : checkoutData?.planLength : 48

	const monthlyPrice = planLength > 1 ? (((displayPrice - totalDownPayment) / planLength) + (checkoutData?.insurancePrice || 0)).toFixed(2) : (((displayPrice) / planLength) + (checkoutData?.insurancePrice || 0)).toFixed(2)
	return (checkoutData || null) &&
		<>

			{totalDownPayment > 0 && <div data-cypress-class-downpayment="downpayment-value"
				className="d-none">
				{totalDownPayment}
			</div>}
			<div className={"row d-block d-md-none mb-3 mb-lg-5"}>
				<div className={"col-12"}>
					<div className={`${isNewDesign ? "bg-grey" : "bg-light"} p-4`}
						style={{borderBottomLeftRadius: `${isNewDesign ? "16px" : ""}`, borderBottomRightRadius: `${isNewDesign ? "16px" : ""}`}}>
						{hideButton && showBackLink ?
							<div>
								{!isNewDesign ? <Link href={t("/checkout")}>
									<a className={"text-dark text-bold"}
										style={{
											textDecoration: "none"
										}}>
										<ArrowLeft size={22}/>
										<Typography variant="bodyLg"
											semanticTag="span"
											className={"ms-2"}>
											{t("Etwas vergessen? Warenkorb bearbeiten.")}
										</Typography>
									</a>
								</Link> :
									null
								}
							</div> : null}
						{children ? <div className={"pb-1 mb-1"}>{children}</div> : null}
						{!payOneTime && <div className={"row mb-3"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Dauer")}{":"}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{`${planLength} ${t("Monate")}`}
							</Typography>
						</div>}
						{checkoutData.discountCode ? <div className={"row text-secondary mb-3"}>
							{process.env.NEXT_PUBLIC_CUSTOM_INSTANCE && organizationSlug && organizationData?.key === "qoqa" && <div style={{padding: "4px 8px", borderRadius: "8px", backgroundColor: organizationData?.key === "qoqa" ? organizationData?.banner.bgColor : ""}}
								className="d-flex align-items-center gap-2 mt-2">
								<img src={organizationData?.listingItem?.customLogo ? organizationData?.listingItem?.customLogo : organizationData?.companyIcon}
									width={"auto"}
									height={18}/>
								<Typography style={{marginTop: -2, color: organizationData?.listingItem?.textColor ? organizationData?.listingItem?.textColor : ""}}
									variant={ "bodyXSm"}
									semanticTag="span">
									{t("code-only-applies-for-total")}
								</Typography>
							</div>}
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Rabattcode")}{":"}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{checkoutData.discountCode}
							</Typography>
						</div> : null}
						{checkoutData.discount ? <div className={"row text-secondary mb-3"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Rabatt")}{":"}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{`CHF ${checkoutData.discount.toFixed(2)}`}
							</Typography>
						</div> : null}
						<div className={"row mb-3"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Zwischensumme")}{":"}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}
								style={{
									color: isOnSale ? data?.planLength < 48 ? redColor : null : null
								}}>
								{`${formatPrice(totalPrice)}`}
							</Typography>
							<Typography className="text-secondary"
								variant="bodyLg"
								semanticTag="div">{t("(inkl. MwSt.)")}</Typography>
						</div>
						{(data?.insurance && data?.insurancePrice) && <div className={"row text-secondary mb-5"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("title-insurance")}<br/>{t(" Calingo")}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}
								style={{
									color: isOnSale ? data?.planLength < 48 ? redColor : null : null
								}}>
								{data?.insurancePrice ? `CHF ${data.insurancePrice.toFixed(2)}` : null}
							</Typography>
						</div>}
						<div className={"row mb-3"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Versandkosten")}{":"}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{t("Kostenlos")}
							</Typography>
						</div>
						<div className={"row border-bottom pb-3 mb-3"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Montage")}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{t("Kostenlos")}
							</Typography>
						</div>
						<div className={"row mb-3"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"}>
								{t("Gesamtkosten")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"}
								style={{
									color: isOnSale ? data?.planLength < 48 ? redColor : null : null
								}}>
								{t(`${formatPrice(displayPrice)}`)}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"text-secondary"}>
								{t("(inkl. Mwst.)")}
							</Typography>
						</div>

						{totalDownPayment > 0 && !payOneTime && <div className={"row text-secondary mb-5"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"}>
								{t("down_payment")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"}>
								{formatPrice(totalDownPayment)}
							</Typography>
						</div>}
						{!payOneTime && planLength > 1 && <div className={"row"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"} >
								{t("Monatliche Kosten")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"}
								style={{
									color: isOnSale ? data?.planLength < 48 ? redColor : null : null
								}}>
								{planLength ? `CHF ${Math.ceil(monthlyPrice).toFixed(2)}` : null}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"text-secondary"}>
								{t("(inkl. Mwst.)")}
							</Typography>
						</div>}
						<div className={"row mb-3"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"}>
								{t("interest")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"} >
								{t("0%")}
							</Typography>
						</div>
					</div>
				</div>
			</div>
			<div className={"row d-none d-md-block"}>
				<div className={"col-12"}>
					<div className={"bg-light p-4"}>
						<Typography variant="heading2"
							semanticTag="h2"
							className={"mb-4"}>
							{t("Übersicht")}
						</Typography>
						{hideButton && showBackLink ?
							<div>
								<Link href={t("/checkout")}>
									<a className={"text-dark text-bold"}
										style={{
											textDecoration: "none"
										}}>
										<ArrowLeft size={22}/>
										<Typography variant="bodyLg"
											semanticTag="span"
											className={"ms-2"}>
											{t("Etwas vergessen? Warenkorb bearbeiten.")}
										</Typography>
									</a>
								</Link>
							</div> : null}
						{children ? <div className={"pb-4 mb-4 border-bottom"}>{children}</div> : null}
						{!payOneTime && <div className={"row text-secondary mb-3"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Dauer")}{":"}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{planLength ? `${planLength} ${t("Monate")}` : null}
							</Typography>
						</div>}
						{checkoutData.discountCode ? <div className={"row text-secondary mb-3"}>
							{process.env.NEXT_PUBLIC_CUSTOM_INSTANCE && organizationSlug && organizationData?.key === "qoqa" && <div style={{padding: "4px 8px", borderRadius: "8px", backgroundColor: organizationData?.key === "qoqa" ? organizationData?.banner.bgColor : ""}}
								className="d-flex align-items-center py-2 gap-2 my-2">
								<img src={organizationData?.listingItem?.customLogo ? organizationData?.listingItem?.customLogo : organizationData?.companyIcon}
									width={"auto"}
									height={18}/>
								<Typography style={{marginTop: -2, color: organizationData?.listingItem?.textColor ? organizationData?.listingItem?.textColor : ""}}
									variant={ "bodyXSm"}
									semanticTag="span">
									{t("code-only-applies-for-total")}
								</Typography>
							</div>}
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Rabattcode")}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{checkoutData.discountCode}
							</Typography>
						</div> : null}
						{checkoutData.discount ? <div className={"row text-secondary mb-3"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"}>
								{t("Rabatt")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"}>
								{`CHF ${checkoutData.discount.toFixed(2)}`}
							</Typography>
						</div> : null}
						{(data?.insurance && data?.insurancePrice > 0) && <div className={"row text-secondary mb-5"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("title-insurance")}<br/>{t(" Calingo")}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{data?.insurancePrice ? `CHF ${data.insurancePrice.toFixed(2)}` : null}
							</Typography>
						</div>}
						<div className={"row text-secondary pb-3 mb-3"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Versandkosten")}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{t("Kostenlos")}
							</Typography>
						</div>
						<div className={"row text-secondary border-bottom pb-3 mb-3"}>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col"}>
								{t("Montage")}
							</Typography>
							<Typography variant="bodyLg"
								semanticTag="div"
								className={"col text-end"}>
								{t("Kostenlos")}
							</Typography>
						</div>
						<div className={"row text-secondary mb-2"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"}>
								{t("Gesamtkosten")}<br/>{t("(inkl. Mwst.)")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"}
								style={{
									color: isOnSale ? data?.planLength < 48 ? redColor : null : null
								}}>
								{t(`${formatPrice(displayPrice)}`)}
							</Typography>
						</div>

						{totalDownPayment > 0 && !payOneTime ? <div className={"row text-secondary mb-2"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"}>
								{t("down_payment")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"}
								style={{
									color: isOnSale ? data?.planLength < 48 ? redColor : null : null
								}}>
								{t(`CHF ${totalDownPayment.toFixed(2)}`)}
							</Typography>
						</div> : null}
						{!payOneTime && <div className={"row text-secondary mb-2"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"} >
								{t("Monatliche Kosten")}<br/>{t("(inkl. Mwst.)")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"}
								style={{
									color: isOnSale ? data?.planLength < 48 ? redColor : null : null
								}}>
								{planLength ? `CHF ${Math.ceil(monthlyPrice).toFixed(2)}` : null}
							</Typography>
						</div>}
						<div className={"row text-secondary mb-5"}>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col"}>
								{t("interest")}<br/>{t("(inkl. Mwst.)")}
							</Typography>
							<Typography variant="bodyLgBold"
								semanticTag="div"
								className={"col text-end"} >
								{t("0%")}
							</Typography>
						</div>
						{checkoutMessage &&
						<div className={"d-flex justify-content-center text-secondary mb-3"}>
							<div className="me-3">
								<Image
									src={"/assets/icons/checkout/check-icon.svg"}
									height={10}
									width={12}
								/>
							</div>
							<Typography variant="bodyLg"
								semanticTag="span">
								{t("checkout-as-fast-as-2-minutes", {ns: "checkout"})}
							</Typography>
						</div>}

						{hideButton ? (
							<div className={"row"}>
								<div className={"col-12"}>
									<div className={"d-grid"}>
										{termsAndConditions &&
										<>
											<TermsAndConditionsCheckbox/>
										</>
										}
										{Object.keys(formik.errors).length > 0 && formik.submitCount > 0 ?
											<div className={"alert alert-danger mt-2"}>
												{t("Bitte überprüfe das Formular. Eines oder mehrere Felder sind noch nicht ausgefüllt.")}
											</div> :
											null}
										{buttonText === OverviewButtonText.continue && !payOneTime ?
											<Button className= "btn btn-primary btn-lg py-3"
												label={t(`${buttonText}`)}
												disabled={formik.isSubmitting || isLoading}
												type={"submit"}
												color="#212529"
												onClick={() => {
													if (typeof window !== "undefined") {
														window.clarity("set", clarityEvents.purchase, "A New Purchase Was Made")
													}
												}}
												textColor="#212529"
												bgColor="#FEC04D"
												isLoading={formik.isSubmitting || isLoading}
											/> :
											// Button for old desktop design
											<Button className= "btn btn-primary btn-lg py-3"
												label={t("Absenden")}
												disabled={formik.isSubmitting || isLoading}
												type={"submit"}
												color="#212529"
												textColor="#212529"
												bgColor="#FEC04D"
												isLoading={formik.isSubmitting || isLoading}
											/>
										}

										{isCheckoutError && <Typography className="text-danger text-center mt-5 mb-4"
											semanticTag="div"
											variant="bodyLgBold">{t("form-submit-error-message", {ns: "checkout"})}</Typography>}
									</div>
								</div>
							</div>
						) : <div className={"d-grid"}
							data-cypress-total-price={`${totalPrice}`}>
							{/* Button desktop */}
							<Button className="data-cypress-checkout"
								disabled={showPriceWarning || (discountCode.isMandatoryForCheckout && (!discountCode.code || discountCode.notApplicable || discountCode.discountAmount === 0))}
								label={t("Zum Checkout")}
								onClick={async () => {
									try {
										if (!inStock) {
											showOutOfStockModal(items?.[0]?.product?.attributes, items?.[0]?.product?.name)
											return
										}

										setForwarding(true)

										const fbEventProducts = []
										data?.deals?.forEach(item => {
											fbEventProducts.push({sku: item?.article_number_2, quantity: 1})
										})

										// Log event
										reactGA?.event({
											category: gaCategories.checkout,
											action: gaEvents.checkoutStepCompleted,
											label: gaEvents.checkoutStepCompleted,
											nonInteraction: false,
											value: 1
										})

										// Log event
										reactGA?.event({
											category: gaCategories.checkout,
											action: gaEvents.checkoutStep1Completed,
											label: gaEvents.checkoutStep1Completed,
											nonInteraction: false
										})

										const ecommerceProducts = []
										data?.deals?.forEach(item => {
											ecommerceProducts.push({item_id: item?.article_number_2, item_name: item?.e_bike_choice})
										})

										reactGA?.gtag(ecommerceEvents.event, ecommerceEvents.beginCheckout, {
											currency: ecommerceEvents.CHF,
											value: totalPrice,
											items: ecommerceProducts
										})
									} catch (error) {
										console.error(error)
									}

									if (router.query.sponsorMeToken) {
										router.push(planLength > 1 ? t(`/checkout/meine-angaben/${router.query.sponsorMeToken}`) : `/checkout-pay-one-time/${router.query.sponsorMeToken}`).then()
									} else {
										router.push(planLength > 1 ? t("/checkout/meine-angaben") : "/checkout-pay-one-time").then()
									}
								}}
								isLoading={forwarding || !isDoneChecking}
								icon={<Lock size={20}/>}
								color="#212529"
								textColor="#212529"
								bgColor="#FEC04D"
								high/>
							{(discountCode.isMandatoryForCheckout && (!discountCode.code || discountCode.notApplicable || discountCode.discountAmount === 0)) ? <Typography className="text-center text-danger mt-2 mb-2"
								variant="bodySm">{t("discount-code-is-required").replace("organization", organizationSlug)}</Typography> : null}
							{planLength > 1 && <>
								<Typography className="text-center mt-2 mb-2"
									variant="bodyLg">{t("monthly-payments-with-value", {value: `${planLength ? Math.ceil(monthlyPrice) : null}`})}</Typography>
								<div className="d-flex align-items-center justify-content-between mb-3">
									<hr className="col-5"/>
									<Typography className="text-uppercase"
										variant="bodyLg">{t("or")}</Typography>
									<hr className="col-5"/>
								</div>
								<Button label={t("pay-one-time")}
									onClick={() => {
										setForwarding(true)
										reactGA?.event({
											category: gaCategories.checkout,
											action: gaEvents.payOneTime,
											label: gaEvents.payOneTime,
											nonInteraction: false
										})
										router.push(t("/checkout-pay-one-time")).then()
									}}
									high
									disabled={(discountCode.isMandatoryForCheckout && (!discountCode.code || discountCode.notApplicable || discountCode.discountAmount === 0))}
									textColor="#212529"
									hoverColor="#212529"
									outline
									className="col-12"
								/>
								<Typography className="text-center mt-2 mb-5"
									variant="bodyLg">{`${t("pay-full-price-chf")} ${formatPrice(totalPrice)}`}</Typography>
							</>}
							<Link href={filterUrl({}, null, null)}
								scroll={false}
								shallow = {true}>
								<div className={planLength > 1 ? "col-auto text-center" : "col-auto text-center mt-5"}>
									<a className="text-decoration-none text-dark col-auto border-bottom border-2 border-primary pointer px-0">
										<Typography className=""
											variant="bodyLgBold"
											semanticTag="span">
											{t("Weiter Einkaufen")}
										</Typography>
									</a>
								</div>
							</Link>
						</div>}
					</div>
				</div>
			</div>
		</>
}

Overview.propTypes = {
	hideButton: PropTypes.bool,
	children: PropTypes.node
}

export default Overview
